import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, withCtx as _withCtx } from "vue"
import _imports_0 from '@/assets.app/images/favorite-demo.gif'


import { onBeforeMount, ref } from 'vue'
import type { Raw } from 'vue'

import BooksBlock from '@/app/components/listing/BooksBlock.vue'
import NoCurrentlyReadingDocsBlock from '@/app/components/listing/NoCurrentlyReadingDocsBlock.vue'
import NoFinishedBooksBlock from '@/app/components/listing/NoFinishedDocsBlock.vue'
import { BooksView } from '@/models/doc.book'
import { filteredDocsByDocType } from '@/models/doc'

import { useWait } from '@/app/composables/useWait'
import { DocSortOption, DocsViewMediator } from '@/models/doc'
import type { Doc, DocsUserDataResponse } from '@/models/interfaces'
import LibraryLayout from '@/app/components/library/LibraryLayout.vue'
import EmptyListingBlock from '@/app/components/listing/EmptyListingBlock.vue'
import { ListView } from '@/models/docs.list'
import { continueReading19038Variant } from '@/services/ab'


export default /*@__PURE__*/_defineComponent({
  __name: 'LibraryBooks',
  props: {
    docs: {},
    docsUserData: {}
  },
  setup(__props: any) {

const props = __props

const wait = useWait()

const currentlyReadingViewRef = ref<Raw<ListView> | null>(null)
const finishedViewRef = ref<Raw<ListView> | null>(null)
const favoritesViewRef = ref<Raw<ListView> | null>(null)
const offlineBooksViewRef = ref<Raw<ListView> | null>(null)
const downloadsViewRef = ref<Raw<ListView> | null>(null)

onBeforeMount(async () => {
  await getBooks()
})

const getBooks = async (): Promise<void> => {
  wait.start('Loading Library')
  try {
    /* TODO: We need to implement offline support for home/docs, #12899.

    if (offline.isOffline) {
       await loadBookListFromNativeStorage()
     } */

    const bookData = filteredDocsByDocType(props.docs, 'book')

    const showCtaBlock = false
    const showFreeAtTheTop = false

    const readingNow = await BooksView.createView(
      bookData,
      props.docsUserData,
      undefined, // Default defaultSort value
      showCtaBlock,
      showFreeAtTheTop,
    )
    // In variant B Reading Now is using History list and shows only
    // the books still in progress, finished books stay reachable through
    // the "View all" link.(Continue Reading Foundations #19038)
    const isContinueReading19038 = continueReading19038Variant() === 'B'
    await readingNow.setList(isContinueReading19038 ? 'history' : 'reading')
    await readingNow.setOnlyInProgress(isContinueReading19038)
    readingNow.enableContinueReading()
    readingNow.enableActionsButton()
    await readingNow.sort(
      isContinueReading19038 ? DocSortOption.viewed : DocSortOption.accessed,
    )
    currentlyReadingViewRef.value = new ListView('Reading Now', readingNow, {
      viewAllLink: isContinueReading19038
        ? '/app/books/list/history'
        : '/app/books/list/reading',
    })

    const finished = await BooksView.createView(
      bookData,
      props.docsUserData,
      DocSortOption.title,
      showCtaBlock,
      showFreeAtTheTop,
    )
    finished.enableActionsButton()
    await finished.setList('finished')
    finishedViewRef.value = new ListView('Finished', finished, {
      viewAllLink: '/app/books/list/finished',
    })

    const favorites = await BooksView.createView(
      bookData,
      props.docsUserData,
      DocSortOption.title,
      showCtaBlock,
      showFreeAtTheTop,
    )
    favorites.enableActionsButton()
    await favorites.setIsFavorite(true)
    favoritesViewRef.value = new ListView('Favorites', favorites, {
      viewAllLink: '/app/books/list/favorites',
    })

    const offlineBooks = await BooksView.createView(
      bookData,
      props.docsUserData,
      undefined, // Default defaultSort value
      showCtaBlock,
      showFreeAtTheTop,
    )
    await offlineBooks.filterByOffline()
    offlineBooksViewRef.value = new ListView('Offline Books', offlineBooks, {
      viewAllLink: '/app/books/list/offline',
    })

    const downloads = await BooksView.createView(
      bookData,
      props.docsUserData,
      DocSortOption.title,
      showCtaBlock,
      showFreeAtTheTop,
    )
    await downloads.filterByExports()
    downloadsViewRef.value = new ListView('Download History', downloads, {
      viewAllLink: '/app/books/list/downloads',
    })

    // The list of book-list views on this page. They all share the same
    // bookData.
    // We create this but we don't need to store it anywhere; the views
    // hold references to it.
    new DocsViewMediator([
      readingNow,
      finished,
      favorites,
      offlineBooks,
      downloads,
    ])

    wait.end('Loading Library')
  } catch (error) {
    if (Sentry) {
      Sentry.captureException(error)
    }
    wait.end('Loading Library')
    wait.start('Error Loading Library')
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(LibraryLayout, null, {
    default: _withCtx(() => [
      (currentlyReadingViewRef.value && currentlyReadingViewRef.value.dataLength)
        ? (_openBlock(), _createBlock(BooksBlock, {
            key: 0,
            view: currentlyReadingViewRef.value,
            showFreeBooks: false
          }, null, 8, ["view"]))
        : (_openBlock(), _createBlock(NoCurrentlyReadingDocsBlock, {
            key: 1,
            title: "Reading now"
          }, {
            default: _withCtx(() => [...(_cache[0] || (_cache[0] = [
              _createElementVNode("p", null, " Books you're currently reading will appear here. Find new things to read on the Discover page! ", -1)
            ]))]),
            _: 1
          })),
      (favoritesViewRef.value && favoritesViewRef.value.dataLength)
        ? (_openBlock(), _createBlock(BooksBlock, {
            key: 2,
            view: favoritesViewRef.value,
            showFreeBooks: false
          }, null, 8, ["view"]))
        : (_openBlock(), _createBlock(EmptyListingBlock, {
            key: 3,
            title: "Favorites",
            class: "empty-favorite"
          }, {
            default: _withCtx(() => [...(_cache[1] || (_cache[1] = [
              _createElementVNode("div", null, [
                _createElementVNode("p", null, "Books you've marked as a Favorite will appear here."),
                _createElementVNode("p", null, " To mark a book as a Favorite, click the bookmark icon on the book card or while reading the book. ")
              ], -1),
              _createElementVNode("img", { src: _imports_0 }, null, -1)
            ]))]),
            _: 1
          })),
      (finishedViewRef.value && finishedViewRef.value.dataLength)
        ? (_openBlock(), _createBlock(BooksBlock, {
            key: 4,
            view: finishedViewRef.value,
            showFreeBooks: false
          }, null, 8, ["view"]))
        : (_openBlock(), _createBlock(NoFinishedBooksBlock, { key: 5 }, {
            default: _withCtx(() => [...(_cache[2] || (_cache[2] = [
              _createElementVNode("p", null, "Books you've finished will appear here.", -1),
              _createElementVNode("p", null, " To mark a book as Finished, click the three dot menu on the book card, then click \"Move to Finished\". ", -1)
            ]))]),
            _: 1
          })),
      (offlineBooksViewRef.value && offlineBooksViewRef.value.dataLength)
        ? (_openBlock(), _createBlock(BooksBlock, {
            key: 6,
            view: offlineBooksViewRef.value,
            "data-test-id": "offline-block",
            showFreeBooks: false
          }, null, 8, ["view"]))
        : _createCommentVNode("", true),
      (downloadsViewRef.value && downloadsViewRef.value.dataLength)
        ? (_openBlock(), _createBlock(BooksBlock, {
            key: 7,
            view: downloadsViewRef.value,
            "data-test-id": "downloads-block",
            showFreeBooks: false
          }, null, 8, ["view"]))
        : _createCommentVNode("", true)
    ]),
    _: 1
  }))
}
}

})